import React, { useState, useEffect } from "react";
import { CreditCard, ExternalLink, CheckCircle, AlertCircle, RefreshCw, Info } from "lucide-react";
import { useAppDispatch, useAppSelector } from "../../redux/store";
import { UpdateUserProfile } from "../../redux/Actions/AuthAction";
import { GlobalService } from "../../modules/shared/services/global.service";
import { stripeConnectAPI, StripeAccount } from "../../services/stripeConnectAPI";
import Button from "./Button";
import Card from "./card";
import StripeConnectInfoDialog from "./StripeConnectInfoDialog";
import "./StripeConnectSetup.css";
import { useTranslation } from "react-i18next";

interface StripeConnectSetupProps {
  onComplete?: () => void;
  onError?: (error: string) => void;
  className?: string;
}

const StripeConnectSetup: React.FC<StripeConnectSetupProps> = ({
  onComplete,
  onError,
  className = "",
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.auth.user);
  
  const [account, setAccount] = useState<StripeAccount | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState<"form" | "onboarding" | "complete">("form");
  const [showInfoDialog, setShowInfoDialog] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    country: "US",
  });

  const isAccountReady = account?.chargesEnabled && account?.payoutsEnabled;

  // Cleanup effect to remove event listeners
  useEffect(() => {
    return () => {
      // Remove any remaining message listeners
      window.removeEventListener("message", () => {});
    };
  }, []);

  // Handle errors
  useEffect(() => {
    if (error && onError) {
      onError(error);
    }
  }, [error, onError]);

  // Check if account is ready
  useEffect(() => {
    if (account && isAccountReady) {
      setStep("complete");
      if (onComplete) {
        onComplete();
      }
    } else if (account && !isAccountReady) {
      setStep("onboarding");
    }
  }, [account, isAccountReady, onComplete]);

  // Initialize form with user email if available
  useEffect(() => {
    if (user?.email) {
      setFormData(prev => ({ ...prev, email: user.email }));
    }
  }, [user]);

  // Check if user has existing Stripe Connect account
  useEffect(() => {
    const checkExistingAccount = async () => {
      // Check if user has stripeConnectedAccountId property (it might not exist in the type yet)
      const stripeAccountId = (user as any)?.stripeConnectedAccountId;
      if (stripeAccountId) {
        try {
          await checkAccountStatus(stripeAccountId);
        } catch (err) {
          console.error("Error checking existing account:", err);
        }
      }
    };

    checkExistingAccount();
  }, [user]);

  // API Functions
  const createAccount = async (email: string, country: string) => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await stripeConnectAPI.createAccount({ email, country });
      
      // Update user profile with new account ID
      const updatedUserData = {
        ...user,
        stripeConnectedAccountId: result.accountId
      };
      
      await dispatch(UpdateUserProfile({ id: user.id, data: updatedUserData }));
      
      setAccount({
        id: result.accountId,
        chargesEnabled: false,
        payoutsEnabled: false,
        detailsSubmitted: false,
      });
      
      GlobalService.showToast("success", t("toast.titles.success"), t("toast.stripeConnect.accountCreatedSuccess"));
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to create account";
      setError(errorMessage);
      GlobalService.showToast("error", t("toast.titles.error"), errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const checkAccountStatus = async (accountId: string) => {
    setLoading(true);
    setError(null);
    
    try {
      const status = await stripeConnectAPI.getAccountStatus(accountId);
      setAccount({
        id: accountId,
        ...status,
      });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to check account status";
      setError(errorMessage);
      console.error("Error checking account status:", err);
    } finally {
      setLoading(false);
    }
  };

  const createOnboardingLink = async (accountId: string): Promise<string> => {
    setLoading(true);
    setError(null);
    
    try {
      // Set return URL to /wallet
      const baseUrl = window.location.origin;
      const return_url = `${baseUrl}/wallet`;
      const refresh_url = `${baseUrl}/wallet`;
      
      const result = await stripeConnectAPI.createAccountLink(accountId, { 
        type: "account_onboarding",
        return_url,
        refresh_url
      });
      return result.url;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to create onboarding link";
      setError(errorMessage);
      GlobalService.showToast("error", t("toast.titles.error"), errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const createUpdateLink = async (accountId: string): Promise<string> => {
    setLoading(true);
    setError(null);
    
    try {
      // Set return URL to /wallet
      const baseUrl = window.location.origin;
      const return_url = `${baseUrl}/wallet`;
      const refresh_url = `${baseUrl}/wallet`;
      
      const result = await stripeConnectAPI.createAccountLink(accountId, { 
        type: "account_update",
        return_url,
        refresh_url
      });
      return result.url;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to create update link";
      setError(errorMessage);
      GlobalService.showToast("error", t("toast.titles.error"), errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const refreshAccountStatus = async () => {
    const stripeAccountId = (user as any)?.stripeConnectedAccountId;
    if (stripeAccountId) {
      try {
        setLoading(true);
        const status = await stripeConnectAPI.getAccountStatus(stripeAccountId);
        
        // Update local account state
        setAccount({
          id: stripeAccountId,
          ...status,
        });
        
        // If account is now ready, update user profile to ensure stripeConnectedAccountId is saved
        if (status.chargesEnabled && status.payoutsEnabled) {
          const updatedUserData = {
            ...user,
            stripeConnectedAccountId: stripeAccountId
          };
          
          await dispatch(UpdateUserProfile({ id: user.id, data: updatedUserData }));
          GlobalService.showToast("success", t("toast.titles.success"), t("toast.stripeConnect.setupCompletedSuccess"));
        }
        
        setError(null);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : "Failed to refresh account status";
        setError(errorMessage);
        console.error("Error refreshing account status:", err);
      } finally {
        setLoading(false);
      }
    } else {
      // If no stripe account ID, try to get it from the account state
      if (account?.id) {
        try {
          setLoading(true);
          const status = await stripeConnectAPI.getAccountStatus(account.id);
          
          setAccount({
            id: account.id,
            ...status,
          });
          
          // Update user profile with the account ID
          if (status.chargesEnabled && status.payoutsEnabled) {
            const updatedUserData = {
              ...user,
              stripeConnectedAccountId: account.id
            };
            
            await dispatch(UpdateUserProfile({ id: user.id, data: updatedUserData }));
            GlobalService.showToast("success", t("toast.titles.success"), t("toast.stripeConnect.setupCompletedSuccess"));
          }
          
          setError(null);
        } catch (err) {
          const errorMessage = err instanceof Error ? err.message : "Failed to refresh account status";
          setError(errorMessage);
          console.error("Error refreshing account status:", err);
        } finally {
          setLoading(false);
        }
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (account) {
      // User already has account, check status
      await checkAccountStatus(account.id);
      setStep("onboarding");
    } else {
      // Create new account
      await createAccount(formData.email, formData.country);
      setStep("onboarding");
    }
  };

  const handleOnboarding = async () => {
    if (!account) return;

    try {
      const linkType = account.detailsSubmitted ? "account_update" : "account_onboarding";
      const url = linkType === "account_onboarding" 
        ? await createOnboardingLink(account.id)
        : await createUpdateLink(account.id);
      
      // Open Stripe onboarding in new window
      const popup = window.open(url, "stripe-onboarding", "width=600,height=700,scrollbars=yes,resizable=yes");
      
      if (!popup) {
        throw new Error("Popup blocked. Please allow popups for this site.");
      }
      
      // Set up message listener for communication with popup
      const handleMessage = (event: MessageEvent) => {
        // Check if message is from Stripe or our redirect page
        if (event.origin !== window.location.origin && !event.origin.includes("stripe.com")) {
          return;
        }
        
        if (event.data && event.data.type === "STRIPE_ONBOARDING_COMPLETE") {
          window.removeEventListener("message", handleMessage);
          // Refresh account status immediately
          setTimeout(() => {
            refreshAccountStatus();
          }, 1000);
        }
      };
      
      window.addEventListener("message", handleMessage);
      
      // Check if popup was closed or redirected
      const checkStatus = setInterval(() => {
        try {
          // Check if popup is closed
          if (popup?.closed) {
            clearInterval(checkStatus);
            window.removeEventListener("message", handleMessage);
            // Refresh account status after popup closes
            setTimeout(() => {
              refreshAccountStatus();
            }, 2000);
            return;
          }
          
          // Check popup URL for completion indicators
          try {
            const popupUrl = popup.location.href;
            if (popupUrl.includes("success") || popupUrl.includes("complete") || popupUrl.includes("return_url")) {
              clearInterval(checkStatus);
              window.removeEventListener("message", handleMessage);
              popup.close();
              // Refresh account status immediately
              setTimeout(() => {
                refreshAccountStatus();
              }, 1000);
            }
          } catch (e) {
            // Cross-origin error is expected, continue monitoring
          }
        } catch (error) {
          // Handle any errors in popup monitoring
          console.warn("Popup monitoring error:", error);
        }
      }, 1000);
      
      // Clean up after 10 minutes to prevent memory leaks
      setTimeout(() => {
        clearInterval(checkStatus);
        window.removeEventListener("message", handleMessage);
        if (popup && !popup.closed) {
          popup.close();
        }
      }, 600000); // 10 minutes
      
    } catch (err) {
      console.error("Onboarding error:", err);
      GlobalService.showToast("error", t("toast.titles.error"), t("toast.stripeConnect.onboardingFailed"));
    }
  };

  const getStatusIcon = (status: boolean) => {
    return status ? (
      <CheckCircle className="h-4 w-4 text-green-500" />
    ) : (
      <AlertCircle className="h-4 w-4 text-yellow-500" />
    );
  };

  const renderStep = () => {
    switch (step) {
      case "form":
        return (
          <div className="stripe-connect-form">
            <div className="flex items-center gap-3 mb-4">
              <CreditCard className="h-6 w-6 text-blue-600" />
              <h3 className="text-lg font-semibold">{t("stripeConnect.setupTitle")}</h3>
            </div>
            <p className="text-gray-600 mb-4">
              {t("stripeConnect.setupDescription")}
            </p>
            
            <div className="mb-6">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setShowInfoDialog(true)}
                className="flex items-center gap-2"
              >
                <Info className="h-4 w-4" />
                {t("stripeConnect.seeMore")}
              </Button>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  {t("stripeConnect.emailLabel")}
                </label>
                <input
                  type="email"
                  id="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  disabled={loading}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
                  placeholder={t("stripeConnect.emailPlaceholder")}
                />
              </div>
              
              <div>
                <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-2">
                  {t("stripeConnect.countryLabel")}
                </label>
                <select
                  id="country"
                  value={formData.country}
                  onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                  disabled={loading}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
                >
                  <option value="US">{t("stripeConnect.countries.US")}</option>
                  <option value="CA">{t("stripeConnect.countries.CA")}</option>
                  <option value="GB">{t("stripeConnect.countries.GB")}</option>
                  <option value="AU">{t("stripeConnect.countries.AU")}</option>
                  <option value="DE">{t("stripeConnect.countries.DE")}</option>
                  <option value="FR">{t("stripeConnect.countries.FR")}</option>
                  <option value="ES">{t("stripeConnect.countries.ES")}</option>
                  <option value="IT">{t("stripeConnect.countries.IT")}</option>
                  <option value="NL">{t("stripeConnect.countries.NL")}</option>
                  <option value="BE">{t("stripeConnect.countries.BE")}</option>
                </select>
              </div>
              
              <Button 
                type="submit" 
                disabled={loading}
                className="w-full"
              >
                {loading ? t("stripeConnect.creatingAccount") : t("stripeConnect.continueToStripe")}
              </Button>
            </form>
          </div>
        );

      case "onboarding":
        return (
          <div className="stripe-connect-onboarding">
            <div className="flex items-center gap-3 mb-4">
              <ExternalLink className="h-6 w-6 text-blue-600" />
              <h3 className="text-lg font-semibold">{t("stripeConnect.completeAccountTitle")}</h3>
            </div>
            <p className="text-gray-600 mb-4">
              {t("stripeConnect.completeAccountDescription")}
            </p>
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <div className="flex items-start gap-3">
                <ExternalLink className="h-5 w-5 text-blue-600 mt-0.5" />
                <div>
                  <h4 className="font-medium text-blue-800 mb-1">{t("stripeConnect.setupInstructions")}</h4>
                  <p className="text-sm text-blue-700 mb-2">
                    {t("stripeConnect.instruction1")}<br/>
                    {t("stripeConnect.instruction2")}<br/>
                    {t("stripeConnect.instruction3")}<br/>
                    {t("stripeConnect.instruction4")}
                  </p>
                  <p className="text-xs text-blue-600">
                    {t("stripeConnect.popupWarning")}
                  </p>
                </div>
              </div>
            </div>
            
            {account && (
              <div className="bg-gray-50 p-4 rounded-lg mb-6">
                <h4 className="font-medium mb-3">{t("stripeConnect.accountStatus")}</h4>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm">{t("stripeConnect.accountDetails")}</span>
                    <div className="flex items-center gap-2">
                      {getStatusIcon(account.detailsSubmitted)}
                      <span className="text-sm">{account.detailsSubmitted ? t("stripeConnect.completed") : t("stripeConnect.pending")}</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">{t("stripeConnect.chargesEnabled")}</span>
                    <div className="flex items-center gap-2">
                      {getStatusIcon(account.chargesEnabled)}
                      <span className="text-sm">{account.chargesEnabled ? t("stripeConnect.enabled") : t("stripeConnect.pending")}</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">{t("stripeConnect.payoutsEnabled")}</span>
                    <div className="flex items-center gap-2">
                      {getStatusIcon(account.payoutsEnabled)}
                      <span className="text-sm">{account.payoutsEnabled ? t("stripeConnect.enabled") : t("stripeConnect.pending")}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            <div className="space-y-3">
              <Button 
                onClick={handleOnboarding} 
                disabled={loading}
                className="w-full"
              >
                {loading ? t("stripeConnect.loading") : t("stripeConnect.completeSetup")}
              </Button>
              
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  onClick={refreshAccountStatus} 
                  disabled={loading}
                  className="flex-1"
                >
                  <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
                  {t("stripeConnect.refreshStatus")}
                </Button>
                
                <Button 
                  variant="outline" 
                  onClick={() => {
                    GlobalService.showToast("info", t("toast.titles.info"), t("toast.stripeConnect.completeSetupInfo"));
                  }}
                  className="flex-1"
                >
                  {t("stripeConnect.needHelp")}
                </Button>
              </div>
            </div>
          </div>
        );

      case "complete":
        return (
          <div className="stripe-connect-complete">
            <div className="flex items-center gap-3 mb-4">
              <CheckCircle className="h-6 w-6 text-green-600" />
              <h3 className="text-lg font-semibold text-green-800">{t("stripeConnect.readyTitle")}</h3>
            </div>
            <p className="text-gray-600 mb-6">
              {t("stripeConnect.readyDescription")}
            </p>
            
            <div className="bg-green-50 p-4 rounded-lg mb-6">
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm font-medium">{t("stripeConnect.accountId")}</span>
                  <span className="text-sm font-mono">{account?.id}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm font-medium">{t("stripeConnect.status")}</span>
                  <span className="text-sm text-green-600">{t("stripeConnect.active")}</span>
                </div>
              </div>
            </div>
            
            <Button 
              variant="outline" 
              onClick={refreshAccountStatus} 
              disabled={loading}
              className="w-full"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
              {t("stripeConnect.refreshStatus")}
            </Button>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <>
      <Card className={`p-6 ${className}`}>
        {renderStep()}
        {error && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center gap-2">
              <AlertCircle className="h-4 w-4 text-red-500" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        )}
      </Card>

           {/* Info Dialog */}
      <StripeConnectInfoDialog 
        isOpen={showInfoDialog} 
        onClose={() => setShowInfoDialog(false)} 
      />
    </>
  );
};

export default StripeConnectSetup;
